import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Car } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const BG_TXT = {
  it: { kicker: "Compatibilità", title: "Scegli il tuo marchio.", desc: "Autoradio dedicate e testate su misura per i marchi più diffusi in Europa. Ogni kit è progettato specificamente per il tuo modello.", all: "Vedi tutte le compatibilità", models: "modelli", catalog: "Vedi catalogo", brand1: "Marchio disponibile", brandN: "Marchi disponibili", carModels: "Modelli auto", shipping: "Spedizione", warranty: "Garanzia", months: "24 mesi", soon: "In arrivo", tiers: { italian: "Italiano", premium: "Premium", popular: "Popolare", utility: "Utility", french: "Francese", japanese: "Giapponese" } },
  en: { kicker: "Compatibility", title: "Choose your brand.", desc: "Dedicated head units, tailored and tested for the most popular brands in Europe. Every kit is designed specifically for your model.", all: "See all compatibilities", models: "models", catalog: "View catalog", brand1: "Brand available", brandN: "Brands available", carModels: "Car models", shipping: "Shipping", warranty: "Warranty", months: "24 months", soon: "Coming soon", tiers: { italian: "Italian", premium: "Premium", popular: "Popular", utility: "Utility", french: "French", japanese: "Japanese" } },
  de: { kicker: "Kompatibilität", title: "Wähle deine Marke.", desc: "Maßgeschneiderte und getestete Autoradios für die beliebtesten Marken Europas. Jedes Kit ist speziell für dein Modell entwickelt.", all: "Alle Kompatibilitäten ansehen", models: "Modelle", catalog: "Katalog ansehen", brand1: "Marke verfügbar", brandN: "Marken verfügbar", carModels: "Automodelle", shipping: "Versand", warranty: "Garantie", months: "24 Monate", soon: "Demnächst", tiers: { italian: "Italienisch", premium: "Premium", popular: "Beliebt", utility: "Nutzfahrzeug", french: "Französisch", japanese: "Japanisch" } },
};

// Brand-specific visual accents. Kept subtle to avoid trademark issues:
// we design a distinctive treatment (color + monogram + font weight) for each brand,
// not the actual manufacturer logo.
// Brand-specific visual accents + official logo (via SimpleIcons CDN, monochrome).
// Displaying manufacturer marks alongside compatibility info is standard aftermarket practice
// (nominative fair use). Brands without a SimpleIcons entry fall back to a typographic monogram.
const LOGO_BASE = "https://cdn.simpleicons.org";
const LOGO_COLOR = "FFFFFF"; // white on colored badge

const BRAND_STYLES = {
  Fiat:           { accent: "#B60000", tier: "italian",  monogram: "F",  logoSlug: "fiat",       wordmarkClass: "font-black tracking-tight" },
  "Alfa Romeo":   { accent: "#0F5C2A", tier: "italian",  monogram: "AR", logoSlug: null,         wordmarkClass: "font-black tracking-tighter italic" },
  Lancia:         { accent: "#0057A5", tier: "italian",  monogram: "L",  logoSlug: null,         wordmarkClass: "font-black tracking-tight italic" },
  DR:             { accent: "#B60000", tier: "italian",  monogram: "DR", logoSlug: null,         wordmarkClass: "font-black tracking-tight" },
  Audi:           { accent: "#111827", tier: "premium",  monogram: "A",  logoSlug: "audi",       wordmarkClass: "font-light tracking-[0.35em]" },
  BMW:            { accent: "#1B4A9E", tier: "premium",  monogram: "B",  logoSlug: "bmw",        wordmarkClass: "font-bold tracking-[0.15em]" },
  Mercedes:       { accent: "#1F1F1F", tier: "premium",  monogram: "M",  logoSlug: null,         wordmarkClass: "font-light tracking-[0.25em] uppercase" },
  Porsche:        { accent: "#1F1F1F", tier: "premium",  monogram: "P",  logoSlug: "porsche",    wordmarkClass: "font-bold tracking-[0.1em]" },
  "Land Rover":   { accent: "#0C3D2E", tier: "premium",  monogram: "LR", logoSlug: "landrover",  wordmarkClass: "font-bold tracking-tight" },
  Volkswagen:     { accent: "#0B4E9C", tier: "popular",  monogram: "VW", logoSlug: "volkswagen", wordmarkClass: "font-semibold tracking-tight" },
  Ford:           { accent: "#1E4F91", tier: "popular",  monogram: "F",  logoSlug: "ford",       wordmarkClass: "font-bold italic tracking-tight" },
  Skoda:          { accent: "#4BA82E", tier: "popular",  monogram: "SK", logoSlug: "skoda",      wordmarkClass: "font-bold tracking-[0.1em]" },
  Seat:           { accent: "#D9202B", tier: "popular",  monogram: "SE", logoSlug: "seat",       wordmarkClass: "font-black tracking-tight uppercase" },
  Mini:           { accent: "#111827", tier: "popular",  monogram: "MI", logoSlug: "mini",       wordmarkClass: "font-bold tracking-tight" },
  Opel:           { accent: "#D4AF37", tier: "utility",  monogram: "O",  logoSlug: "opel",       wordmarkClass: "font-black tracking-tight" },
  Renault:        { accent: "#FFCA00", tier: "utility",  monogram: "R",  logoSlug: "renault",    wordmarkClass: "font-black tracking-tighter" },
  Dacia:          { accent: "#003D5B", tier: "utility",  monogram: "D",  logoSlug: "dacia",      wordmarkClass: "font-bold tracking-[0.05em]" },
  Smart:          { accent: "#B60000", tier: "utility",  monogram: "SM", logoSlug: null,         wordmarkClass: "font-bold tracking-tight lowercase" },
  Iveco:          { accent: "#003D82", tier: "utility",  monogram: "IV", logoSlug: null,         wordmarkClass: "font-black tracking-tight" },
  Peugeot:        { accent: "#1F2937", tier: "french",   monogram: "P",  logoSlug: "peugeot",    wordmarkClass: "font-bold tracking-[0.05em]" },
  "Citroën":      { accent: "#C8102E", tier: "french",   monogram: "C",  logoSlug: "citroen",    wordmarkClass: "font-black tracking-tight" },
  Toyota:         { accent: "#EB0A1E", tier: "japanese", monogram: "T",  logoSlug: "toyota",     wordmarkClass: "font-bold tracking-[0.1em]" },
  Nissan:         { accent: "#C3002F", tier: "japanese", monogram: "N",  logoSlug: "nissan",     wordmarkClass: "font-bold tracking-[0.2em] uppercase" },
  Mazda:          { accent: "#101F32", tier: "japanese", monogram: "MZ", logoSlug: "mazda",      wordmarkClass: "font-bold tracking-[0.1em]" },
  Honda:          { accent: "#CE0F13", tier: "japanese", monogram: "H",  logoSlug: "honda",      wordmarkClass: "font-black tracking-[0.15em] uppercase" },
  Suzuki:         { accent: "#0055A4", tier: "japanese", monogram: "S",  logoSlug: "suzuki",     wordmarkClass: "font-bold italic tracking-tight" },
  Subaru:         { accent: "#1A3667", tier: "japanese", monogram: "SB", logoSlug: "subaru",     wordmarkClass: "font-bold tracking-tight" },
  Mitsubishi:     { accent: "#E60012", tier: "japanese", monogram: "MI", logoSlug: "mitsubishi", wordmarkClass: "font-bold tracking-tight" },
  SsangYong:      { accent: "#00458C", tier: "japanese", monogram: "SY", logoSlug: null,         wordmarkClass: "font-bold tracking-tight" },
  Isuzu:          { accent: "#E60012", tier: "japanese", monogram: "IS", logoSlug: null,         wordmarkClass: "font-bold tracking-tight" },
  Kia:            { accent: "#05141F", tier: "korean",   monogram: "K",  logoSlug: "kia",        wordmarkClass: "font-black tracking-[0.1em]" },
  Hyundai:        { accent: "#002C5F", tier: "korean",   monogram: "H",  logoSlug: "hyundai",    wordmarkClass: "font-bold tracking-[0.05em]" },
  Jeep:           { accent: "#374151", tier: "american", monogram: "J",  logoSlug: "jeep",       wordmarkClass: "font-black tracking-[0.25em] uppercase" },
  Chevrolet:      { accent: "#B18F3B", tier: "american", monogram: "CH", logoSlug: "chevrolet",  wordmarkClass: "font-bold tracking-[0.1em] uppercase" },
};

const TIER_LABEL = {
  italian: "Italiano",
  premium: "Premium",
  popular: "Popolare",
  utility: "Utility",
  french: "Francese",
  japanese: "Giapponese",
  korean: "Coreano",
  american: "Americano",
};

export default function BrandsGrid() {
  const [items, setItems] = useState([]);
  const { lang } = useLang();
  const T = BG_TXT[lang] || BG_TXT.en;

  useEffect(() => {
    api.get("/vehicles/brands-summary").then((r) => setItems(r.data));
  }, []);

  // Only display brands that already have at least one product in stock.
  // Brands without products are kept in the roadmap counter below.
  const available = items.filter((b) => b.products_count > 0);
  const upcoming = items.filter((b) => b.products_count === 0);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
      <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
        <div>
          <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-cyanHover">{T.kicker}</div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-xd-text mt-1">{T.title}</h2>
          <p className="text-sm text-xd-muted mt-2 max-w-xl">
            {T.desc}
          </p>
        </div>
        <Link to="/catalogo" className="text-sm font-medium text-xd-cyanHover hover:underline inline-flex items-center gap-1 whitespace-nowrap">
          {T.all} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {available.map((b) => {
          const style = BRAND_STYLES[b.brand] || { accent: "#17C7D6", tier: "popular", monogram: b.brand.slice(0, 2).toUpperCase(), logoSlug: null, wordmarkClass: "font-bold" };
          const logoUrl = style.logoSlug ? `${LOGO_BASE}/${style.logoSlug}/${LOGO_COLOR}` : null;
          return (
            <Link
              key={b.brand}
              to={`/marca/${encodeURIComponent(b.brand.toLowerCase().replace(/\s+/g, '-'))}`}
              data-testid={`brand-card-${b.brand}`}
              className="group relative bg-white border border-xd-border rounded-xl overflow-hidden transition-all duration-300 hover:border-xd-cyan hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-20px_rgba(11,18,32,0.15)]"
            >
              {/* Accent bar */}
              <div className="h-1" style={{ background: style.accent }} aria-hidden />

              <div className="p-5 sm:p-6">
                <div className="flex items-start justify-between">
                  {/* Logo badge (falls back to monogram if no SimpleIcons slug or image fails to load) */}
                  <div
                    className="relative w-11 h-11 rounded-xl flex items-center justify-center shadow-sm overflow-hidden"
                    style={{ background: style.accent }}
                    aria-label={b.brand}
                  >
                    <span
                      className="absolute inset-0 flex items-center justify-center text-white font-mono-tech font-bold text-sm pointer-events-none"
                      aria-hidden
                    >
                      {style.monogram}
                    </span>
                    {logoUrl && (
                      <img
                        src={logoUrl}
                        alt=""
                        className="relative w-6 h-6 z-10 object-contain"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    )}
                  </div>
                  <span className="text-[10px] font-mono-tech uppercase tracking-widest text-xd-muted px-2 py-0.5 rounded bg-xd-light">
                    {T.tiers[style.tier] || TIER_LABEL[style.tier]}
                  </span>
                </div>

                {/* Wordmark */}
                <div className={`font-display mt-5 text-2xl text-xd-text ${style.wordmarkClass}`}>
                  {b.brand}
                </div>

                {/* Stats */}
                <div className="mt-4 pt-4 border-t border-xd-border flex items-center justify-between font-mono-tech">
                  <div className="text-xs text-xd-muted">
                    <span className="font-semibold text-xd-text">{b.products_count}</span> {b.products_count === 1 ? "radio" : "radio"}
                  </div>
                  <div className="text-xs text-xd-muted flex items-center gap-1">
                    <Car className="w-3 h-3" />
                    <span className="font-semibold text-xd-text">{b.supported_models}</span> {T.models}
                  </div>
                </div>

                {/* CTA arrow */}
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs font-semibold text-xd-cyanHover group-hover:text-xd-orange transition-colors">
                    {T.catalog}
                  </span>
                  <ArrowRight className="w-4 h-4 text-xd-muted group-hover:text-xd-orange group-hover:translate-x-0.5 transition-all" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Bottom stats bar */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 bg-white border border-xd-border rounded-xl p-5 sm:p-6">
        <Stat value={String(available.length)} label={available.length === 1 ? T.brand1 : T.brandN} />
        <Stat value={String(items.reduce((s, b) => s + b.supported_models, 0)) + "+"} label={T.carModels} />
        <Stat value="24-48h" label={T.shipping} />
        <Stat value={T.months} label={T.warranty} />
      </div>

      {/* Upcoming brands (roadmap) */}
      {upcoming.length > 0 && (
        <div data-testid="brands-upcoming" className="mt-6 flex items-center gap-3 flex-wrap justify-center sm:justify-start rounded-xl border border-dashed border-xd-border bg-white/60 px-5 py-4">
          <span className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted whitespace-nowrap">{T.soon}</span>
          <div className="flex items-center gap-2 flex-wrap">
            {upcoming.map((b) => (
              <span key={b.brand} className="inline-flex items-center gap-2 text-xs bg-xd-light border border-xd-border rounded-full px-3 py-1 text-xd-muted">
                <span className="w-1.5 h-1.5 rounded-full bg-xd-cyan animate-pulse" />
                {b.brand}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div className="text-center sm:text-left border-l-0 sm:border-l sm:first:border-l-0 border-xd-border sm:pl-4 first:pl-0">
      <div className="font-display text-2xl sm:text-3xl font-semibold text-xd-text">{value}</div>
      <div className="text-xs text-xd-muted mt-0.5">{label}</div>
    </div>
  );
}
